import type { LucideIcon } from 'lucide-react'
import {
  FileText,
  Zap,
  Calendar,
  MessageSquare,
  HelpCircle,
  Wallet,
  User,
  MapPin,
  Bell,
  Briefcase,
  ClipboardList,
  Plus,
  Receipt,
  Calculator,
  ShieldCheck,
  BarChart3,
  ShoppingBag,
  Store,
} from 'lucide-react'

export type UserPanelRole = 'INSTALLER' | 'CUSTOMER'

export interface UserMenuItem {
  label: string
  href: string
  icon: LucideIcon
  /** Numeric badge or short text label rendered next to the item. */
  badge?: string | number
  /** Used for fuzzy matching in command palette. */
  keywords?: string[]
  /** Short description displayed in command palette and tooltips. */
  description?: string
  /** Visual emphasis – primary actions are highlighted differently. */
  emphasis?: 'primary' | 'default'
  /**
   * When true, this item is only shown to installers whose verification
   * status is APPROVED (spec FR2/FR8). Items without this flag are visible
   * in every status (Dashboard, Profile, Analytics, Support, Logout…).
   */
  requiresApproved?: boolean
  /**
   * When true, this item is only shown while the installer still needs
   * first-time onboarding. Once onboarding is submitted/approved/rejected the
   * wizard lives inside the profile page, so this standalone link is hidden.
   */
  requiresOnboarding?: boolean
}

export interface UserMenuGroup {
  /** Stable identifier (used for collapse persistence). */
  id: string
  label: string
  /** Section icon – rendered next to the group heading. */
  icon: LucideIcon
  items: UserMenuItem[]
  /** Default collapsed state on first render. */
  defaultCollapsed?: boolean
}

function normalizeRole(role?: string | null): UserPanelRole {
  return role === 'INSTALLER' ? 'INSTALLER' : 'CUSTOMER'
}

export function getDashboardPath(role?: string | null): string {
  return normalizeRole(role) === 'INSTALLER' ? '/installer/dashboard' : '/dashboard'
}

export interface MenuFactoryParams {
  invoiceLabel: string
  walletLabel: string
}

export function getUserMenuGroups(
  role: string | null | undefined,
  params: MenuFactoryParams,
  t: (key: string) => string,
): UserMenuGroup[] {
  const normalizedRole = normalizeRole(role)

  if (normalizedRole === 'INSTALLER') {
    return [
      {
        id: 'installer-account',
        label: t('installerAccount'),
        icon: User,
        items: [
          { label: t('profile'), href: '/installer/profile', icon: User, keywords: ['پروفایل', 'profile'] },
          { label: t('notifications'), href: '/notifications', icon: Bell, keywords: ['نوتیف', 'اعلان', 'notifications'] },
          {
            label: t('analytics'),
            href: '/installer/analytics',
            icon: BarChart3,
            description: t('analyticsDescription'),
            keywords: ['آمار', 'تحلیل', 'analytics', 'نمودار', 'گزارش'],
          },
          {
            label: t('onboarding'),
            href: '/installer/onboarding',
            icon: ClipboardList,
            description: t('onboardingDescription'),
            keywords: ['onboarding', 'متخصص', 'تکمیل'],
            // After submission the wizard lives inside «پروفایل»; this
            // standalone link is only for genuine first-time onboarding.
            requiresOnboarding: true,
          },
        ],
      },
      {
        id: 'installer-ops',
        label: t('installerOperations'),
        icon: Briefcase,
        items: [
          {
            label: t('orderPanel'),
            href: '/installer/orders?tab=recommended',
            icon: Briefcase,
            emphasis: 'primary',
            description: t('orderPanelDescription'),
            keywords: ['درخواست مشاوره', 'پنل', 'هاب', 'متخصص', 'پیشنهاد', 'فعال'],
            requiresApproved: true,
          },
          {
            label: t('activeOrders'),
            href: '/installer/orders?tab=open',
            icon: ClipboardList,
            keywords: ['فعال', 'open', 'در حال انجام'],
            requiresApproved: true,
          },
          {
            label: t('pendingSupervisor'),
            href: '/installer/orders?tab=pending-supervisor',
            icon: ShieldCheck,
            keywords: ['سرپرست', 'انتظار', 'تأیید'],
            requiresApproved: true,
          },
          {
            label: t('completed'),
            href: '/installer/orders?tab=completed',
            icon: FileText,
            keywords: ['تکمیل', 'completed', 'درآمد'],
            requiresApproved: true,
          },
          // {
          //   label: 'بازدیدهای برنامه‌ریزی شده',
          //   href: '/solar/visits',
          //   icon: Calendar,
          //   keywords: ['بازدید', 'visit'],
          //   requiresApproved: true,
          // },
        ],
      },
      {
        id: 'installer-support',
        label: t('support'),
        icon: MessageSquare,
        items: [
          { label: t('tickets'), href: '/support/tickets', icon: MessageSquare, keywords: ['تیکت', 'پشتیبانی', 'ticket'] },
          { label: t('newTicket'), href: '/support/tickets/new', icon: HelpCircle, keywords: ['تیکت جدید'] },
        ],
      },
      {
        id: 'installer-financial',
        label: t('finance'),
        icon: Wallet,
        items: [
          {
            label: params.invoiceLabel,
            href: '/billing/invoices',
            icon: FileText,
            keywords: ['فاکتور', 'invoice', 'صورتحساب'],
            requiresApproved: true,
          },
          {
            label: params.walletLabel,
            href: '/installer/financial/wallet',
            icon: Wallet,
            description: t('walletDescription'),
            keywords: ['کیف پول', 'wallet', 'موجودی', 'برداشت', 'تسویه'],
            requiresApproved: true,
          },
          {
            label: t('transactions'),
            href: '/installer/financial/transactions',
            icon: Receipt,
            description: t('transactionsDescription'),
            keywords: ['تراکنش', 'transaction', 'گزارش', 'سابقه', 'اکسل'],
            requiresApproved: true,
          },
        ],
      },
    ]
  }

  return [
    {
      id: 'account',
      label: t('account'),
      icon: User,
      items: [
        { label: t('profile'), href: '/account/profile', icon: User, keywords: ['پروفایل', 'profile'] },
        {
          label: t('addresses'),
          href: '/account/addresses',
          icon: MapPin,
          keywords: ['آدرس', 'مکان', 'address'],
        },
        {
          label: t('notifications'),
          href: '/notifications',
          icon: Bell,
          keywords: ['نوتیف', 'اعلان', 'notifications'],
        },
      ],
    },
    {
      id: 'orders',
      label: t('orders'),
      icon: FileText,
      items: [
        {
          label: t('myOrders'),
          href: '/orders',
          icon: FileText,
          emphasis: 'primary',
          description: t('myOrdersDescription'),
          keywords: ['درخواست مشاوره', 'order', 'درخواست', 'مشاوره', 'نصب', 'قرارداد', 'پیشنهاد', 'بازخورد'],
        },
        {
          label: t('newOrder'),
          href: '/orders/new',
          icon: Plus,
          emphasis: 'primary',
          keywords: ['ثبت درخواست مشاوره', 'new order', 'درخواست جدید'],
        },
        {
          label: t('storeOrders'),
          href: '/store/orders',
          icon: ShoppingBag,
          description: t('storeOrdersDescription'),
          keywords: ['سفارش', 'فروشگاه', 'store', 'order', 'خرید', 'فاکتور'],
        },
        {
          label: t('seller'),
          href: '/seller',
          icon: Store,
          description: t('sellerDescription'),
          keywords: ['فروشنده', 'seller', 'پنل فروشنده', 'فروشگاه من', 'محصولات من'],
        },
        // { label: t('estimations'), href: '/solar/estimation', icon: Zap, keywords: ['تخمین', 'estimation'] },
        // { label: t('visits'), href: '/solar/visits', icon: Calendar, keywords: ['بازدید', 'visit'] },
      ],
    },
    {
      id: 'support',
      label: t('support'),
      icon: MessageSquare,
      items: [
        { label: t('tickets'), href: '/support/tickets', icon: MessageSquare, keywords: ['تیکت', 'ticket'] },
        { label: t('newTicket'), href: '/support/tickets/new', icon: HelpCircle, keywords: ['تیکت جدید'] },
      ],
    },
    {
      id: 'finance',
      label: t('finance'),
      icon: Wallet,
      items: [
        {
          label: params.invoiceLabel,
          href: '/billing/invoices',
          icon: Receipt,
          keywords: ['فاکتور', 'invoice', 'صورتحساب'],
        },
        { label: params.walletLabel, href: '/wallet', icon: Wallet, keywords: ['کیف پول', 'wallet'] },
      ],
    },
    {
      id: 'tools',
      label: t('tools'),
      icon: Calculator,
      defaultCollapsed: true,
      items: [
        {
          label: t('calculator'),
          href: '/calculator',
          icon: Calculator,
          keywords: ['ماشین حساب', 'calculator', 'انرژی'],
        },
      ],
    },
  ]
}

/* ============================================================
 * Bottom navigation – mobile-first, 4-5 items max.
 * ============================================================ */
export interface BottomNavItem {
  label: string
  shortLabel?: string
  href: string
  icon: LucideIcon
  /** When `true`, treated as primary FAB-like action (rendered larger). */
  primary?: boolean
}

export function getBottomNavItems(role: string | null | undefined, t: (key: string) => string): BottomNavItem[] {
  const dashboardPath = getDashboardPath(role)
  const normalizedRole = normalizeRole(role)
  if (normalizedRole === 'INSTALLER') {
    return [
      { label: t('dashboard'), href: dashboardPath, icon: Briefcase },
      {
        label: t('orders'),
        href: '/installer/orders?tab=recommended',
        icon: FileText,
        primary: true,
      },
      // { label: 'بازدیدها', href: '/solar/visits', icon: Calendar },
      { label: t('notifications'), href: '/notifications', icon: Bell },
      { label: t('profile'), href: '/account/profile', icon: User },
    ]
  }
  return [
    { label: t('dashboard'), href: dashboardPath, icon: User },
    { label: t('orders'), href: '/orders', icon: FileText },
    { label: t('createOrder'), shortLabel: t('new'), href: '/orders/new', icon: Plus, primary: true },
    { label: t('wallet'), href: '/wallet', icon: Wallet },
    { label: t('notifications'), href: '/notifications', icon: Bell },
  ]
}

export const USER_PANEL_PATH_PREFIXES = [
  '/dashboard',
  '/installer',
  '/account',
  '/solar',
  '/orders',
  '/support',
  '/billing',
  '/wallet',
  '/notifications',
] as const

export function isUserPanelPath(pathname: string): boolean {
  return USER_PANEL_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'))
}
